import ArrowBackIosIcon from '@mui/icons-material/ArrowBackIos';
import ArrowForwardIosIcon from '@mui/icons-material/ArrowForwardIos';
import Tooltip from '@mui/material/Tooltip';
import { abyssColors } from '@/theme/colors';
import { SxProps, Box, Stack, IconButton } from '@mui/material';
import { ReactNode, useCallback, useEffect, useState } from 'react';
import { NavigationItem } from '@/types';

const TOOLTIP_MESSAGES = {
  onlyOneItem: 'Only 1 item is available',
  firstItem: 'Already at the first item',
  lastItem: 'Already at the last item',
};

type Props = {
  sx?: SxProps;
  showTooltipTitle?: boolean;
  children: ReactNode;
  handleChange: (data: Partial<NavigationItem>) => void;
  content?: ReactNode;
  items: NavigationItem[];
  selectedItemId?: string;
  toolTip?: string;
  location?: string;
};

export const Navigation = ({
  children,
  items,
  handleChange,
  content,
  selectedItemId,
  toolTip,
  sx,
  location = '',
}: Props) => {
  const [currentIndex, setCurrentIndex] = useState(() => {
    if (!items?.length || !selectedItemId) return -1;
    return items.findIndex((item) => item?.id === selectedItemId);
  });

  const isDisabled = !items?.length || items.length === 1;
  const isPreviousDisabled = isDisabled || currentIndex <= 0;
  const isNextDisabled = isDisabled || currentIndex === -1 || currentIndex >= items.length - 1;

  const handlePrevious = useCallback(() => {
    if (isPreviousDisabled) return;
    const newIndex = currentIndex - 1;
    const currentData = items[newIndex];
    setCurrentIndex(newIndex);
    handleChange({ name: currentData.name, id: currentData.id });
  }, [currentIndex, items, handleChange, isPreviousDisabled]);

  const handleNext = useCallback(() => {
    if (isNextDisabled) return;
    const newIndex = currentIndex + 1;
    const currentData = items[newIndex];
    setCurrentIndex(newIndex);
    handleChange({ name: currentData.name, id: currentData.id });
  }, [currentIndex, items, handleChange, isNextDisabled]);

  useEffect(() => {
    const handler = (e: KeyboardEvent) => {
      if (!e.ctrlKey) return;
      if (e.key === 'ArrowLeft' && !isPreviousDisabled) handlePrevious();
      if (e.key === 'ArrowRight' && !isNextDisabled) handleNext();
    };
    window.addEventListener('keydown', handler);
    return () => window.removeEventListener('keydown', handler);
  }, [handlePrevious, handleNext, isPreviousDisabled, isNextDisabled]);

  const getPrevTooltip = () => {
    if (isDisabled) return TOOLTIP_MESSAGES.onlyOneItem;
    if (currentIndex <= 0) return TOOLTIP_MESSAGES.firstItem;
    return '';
  };

  const getNextTooltip = () => {
    if (isDisabled) return TOOLTIP_MESSAGES.onlyOneItem;
    if (currentIndex === -1 || currentIndex >= items.length - 1) return TOOLTIP_MESSAGES.lastItem;
    return '';
  };

  return (
    <Stack direction="row" alignItems="center" spacing={0.5} sx={sx}>
      <Tooltip title={isPreviousDisabled ? getPrevTooltip() : ''} arrow placement="top">
        <span>
          <IconButton onClick={handlePrevious} disabled={isPreviousDisabled}>
            <ArrowBackIosIcon
              sx={{
                color: isPreviousDisabled ? abyssColors.neutral[400] : abyssColors.primary[500],
                height: '2rem',
              }}
              id={`${location}-previous-button`}
            />
          </IconButton>
        </span>
      </Tooltip>

      <Tooltip title={toolTip} arrow placement="top">
        <Box>{children}</Box>
      </Tooltip>

      <Tooltip title={isNextDisabled ? getNextTooltip() : ''} arrow placement="top">
        <span>
          <IconButton onClick={handleNext} disabled={isNextDisabled}>
            <ArrowForwardIosIcon
              sx={{
                color: isNextDisabled ? abyssColors.neutral[400] : abyssColors.primary[500],
                height: '2rem',
              }}
              id={`${location}-next-button`}
            />
          </IconButton>
        </span>
      </Tooltip>

      {content && <Box>{content}</Box>}
    </Stack>
  );
};
